import {
  getDocs, getDoc, query, orderBy, limit, startAfter, where, onSnapshot, doc,
  updateDoc, deleteField,
  type QueryDocumentSnapshot, type DocumentData, type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import * as Sentry from '@sentry/react'
import { orgEligibilityChecksCollection } from './firestore'
import { db, functions } from '../config'
import type { VerifyEligibilityResponse } from '../../shared/verifyEligibilityResponse'
import type { EligibilityCheckDoc, CockpitEligibilityStatus, CorrectionOverride, UserPickOverride, LegacyCollectionResult } from '../types'
import { readTerminal } from '../utils/terminalStateBadge'
import { MAX_RETRIES } from '../../shared/retryPolicy'
import { isAutoRetrying } from '../../shared/eligibilityDisplayStatus'
import { isCheckStale } from '../../shared/eligibilityFreshness'
import { logStaffEdit } from './audit'

/** Default page size for recent checks queries. */
export const RECENT_CHECKS_PAGE_SIZE = 25

/**
 * Bridge V2 storage → canonical render keys: if a check carries the V2 keys
 * (`resultV2` / `collectionResultV2`), promote them onto the canonical
 * `result` / `collectionResult` the UI renders; otherwise return the doc
 * untouched.
 *
 * The V2 engine writes only the *V2 keys (see functions executeCheck), leaving
 * canonical empty — so a rollback to the pre-V2 build reads empty canonical and
 * degrades gracefully instead of choking on the new shape. Pre-V2 docs carry no
 * *V2 keys, so they pass through with their canonical fields intact.
 */
function preferV2(doc: EligibilityCheckDoc): EligibilityCheckDoc {
  const { resultV2, collectionResultV2 } = doc
  if (!resultV2 && !collectionResultV2) return doc
  return {
    ...doc,
    ...(resultV2 ? { result: resultV2 } : {}),
    ...(collectionResultV2 ? { collectionResult: collectionResultV2 } : {}),
  }
}

/**
 * Present a flow key on every check doc: `flowId ?? eligibilityCheckSetId ??
 * id`. Docs written before flows existed lack the field — their setId is
 * exactly what the backfill stamps, so deriving it here keeps every consumer
 * (slideout re-run threading, prefill) working on one field with no per-screen
 * fallbacks. The doc's own id is the last resort (pre-set legacy docs).
 */
function withFlowKey(doc: EligibilityCheckDoc): EligibilityCheckDoc {
  if (doc.flowId) return doc
  return { ...doc, flowId: doc.eligibilityCheckSetId ?? doc.id }
}

/**
 * Move a pre-PaymentOption `collectionResult` onto `legacyCollectionResult` so
 * the slideout can render it with the legacy card. The new schema nests options
 * under `inNetwork`/`outOfNetwork`; legacy docs carry a top-level `action` field
 * and no `inNetwork` key. We relocate (rather than drop) the legacy payload and
 * clear the new-shape `collectionResult`/`userPick` channels — `userPick`
 * targets PaymentOption ids that legacy results don't have.
 *
 * Runs `preferV2` first so any promoted V2 output is normalized like the rest
 * (a doc with both legacy and V2 prefers V2 and never reaches the branch below).
 */
function normalizeLegacy(input: EligibilityCheckDoc): EligibilityCheckDoc {
  const doc = preferV2(withFlowKey(input))
  const cr = doc.collectionResult as unknown
  const isLegacy = !!cr && typeof cr === 'object' && (
    'action' in (cr as object) || !('inNetwork' in (cr as object))
  )
  if (!isLegacy) return doc
  const { collectionResult: legacy, userPick: _up, ...rest } = doc
  return {
    ...rest,
    legacyCollectionResult: legacy as unknown as LegacyCollectionResult,
  } as EligibilityCheckDoc
}

/**
 * The one normalization every reader here applies before a stored check is
 * rendered: V2 keys promoted onto the canonical ones, a flow key present, a
 * pre-PaymentOption collection result relocated to the legacy card. Exported
 * for the staff console's Payer Mappings board, which receives a check
 * through a callable rather than this module's queries and must show it
 * exactly as the Cockpit and Recent Checks do.
 */
export function normalizeStoredCheck(doc: EligibilityCheckDoc): EligibilityCheckDoc {
  return normalizeLegacy(doc)
}

export interface RecentChecksPage {
  docs: EligibilityCheckDoc[]
  /**
   * Cursor for the next page — either the last doc snapshot (legacy one-shot
   * path) or the last `checkedAt` string (subscription path where snapshots
   * aren't held on the client). Null when fewer than pageSize results.
   */
  lastDoc: QueryDocumentSnapshot<DocumentData> | string | null
}

/**
 * Fetch a page of recent eligibility checks.
 * Pass `cursor` from a previous page to fetch the next batch.
 * `cursor` may be either a document snapshot or a `checkedAt` string value
 * (Firestore's startAfter accepts both when paginating on a single orderBy
 * field). Subscription-based flows pass the string form.
 * Optionally filter by `clinicId`.
 * `sortDir` controls checkedAt ordering — defaults to 'desc' (newest first).
 */
export async function listRecentChecks(
  orgId: string,
  pageSize = RECENT_CHECKS_PAGE_SIZE,
  cursor?: QueryDocumentSnapshot<DocumentData> | string | null,
  clinicId?: string,
  sortDir: 'asc' | 'desc' = 'desc',
  windowStart?: Date | null,
): Promise<RecentChecksPage> {
  const ref = orgEligibilityChecksCollection(orgId)
  const constraints: QueryConstraint[] = []
  if (clinicId) {
    constraints.push(where('clinicId', '==', clinicId))
  }
  if (windowStart) {
    constraints.push(where('checkedAt', '>=', windowStart.toISOString()))
  }
  constraints.push(orderBy('checkedAt', sortDir))
  if (cursor) {
    constraints.push(startAfter(cursor))
  }
  constraints.push(limit(pageSize))

  const q = query(ref, ...constraints)
  const snap = await getDocs(q)
  const docs = snap.docs.map(d => normalizeLegacy({ id: d.id, ...d.data() } as EligibilityCheckDoc))
  const lastDoc = snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null

  return { docs, lastDoc }
}

/**
 * Subscribe to the first page of recent eligibility checks.
 * Uses onSnapshot so pending rows flip to success/error live when the webhook
 * resolves — callers don't need per-row subscriptions.
 *
 * For "load more" pagination, keep using listRecentChecks with a cursor.
 * Only the first page is live; older pages stay static.
 */
export function subscribeToRecentChecks(
  orgId: string,
  onUpdate: (docs: EligibilityCheckDoc[]) => void,
  onError: (err: Error) => void,
  pageSize = RECENT_CHECKS_PAGE_SIZE,
  clinicId?: string,
  sortDir: 'asc' | 'desc' = 'desc',
  windowStart?: Date | null,
): () => void {
  const ref = orgEligibilityChecksCollection(orgId)
  const constraints: QueryConstraint[] = []
  if (clinicId) {
    constraints.push(where('clinicId', '==', clinicId))
  }
  if (windowStart) {
    constraints.push(where('checkedAt', '>=', windowStart.toISOString()))
  }
  constraints.push(orderBy('checkedAt', sortDir))
  constraints.push(limit(pageSize))
  const q = query(ref, ...constraints)
  return onSnapshot(q, (snap) => {
    const docs = snap.docs.map(d => normalizeLegacy({ id: d.id, ...d.data() } as EligibilityCheckDoc))
    onUpdate(docs)
  }, onError)
}

/**
 * Subscribe to a single eligibility check document for real-time updates.
 * Used to receive VSP RPA bot results as they arrive via webhook.
 * Returns an unsubscribe function — call it when the component unmounts.
 */
export function subscribeToEligibilityCheck(
  orgId: string,
  checkId: string,
  onUpdate: (check: EligibilityCheckDoc | null) => void,
): () => void {
  const ref = doc(db, 'organizations', orgId, 'eligibilityChecks', checkId)
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      onUpdate(null)
      return
    }
    onUpdate(normalizeLegacy({ id: snap.id, ...snap.data() } as EligibilityCheckDoc))
  })
}

/**
 * One-shot fetch of a single eligibility check by ID — the resolver behind the
 * permalink deep-link (`?check=<id>`). A direct doc read by ID, so it bypasses
 * the recent-window query in listRecentChecks/subscribeToRecentChecks: #83
 * staleness is display-only and the doc is never deleted, so a saved link still
 * resolves after the check ages out of the default view. A plain getDoc (not a
 * subscription) because a filed record is point-in-time — we render the payer
 * response as stored, not live. Returns null when the doc doesn't exist; throws
 * on a Firestore permission error (caller treats both as "not available").
 */
export async function getEligibilityCheck(
  orgId: string,
  checkId: string,
): Promise<EligibilityCheckDoc | null> {
  const ref = doc(db, 'organizations', orgId, 'eligibilityChecks', checkId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  // Bridge V2 storage keys onto canonical `result`/`collectionResult`, same as
  // every other read path. Without this, a V2-written check (which stores only
  // `resultV2`/`collectionResultV2` and leaves canonical empty) resolves with an
  // empty `result`, so the `?check=<id>` deep-link reports "no saved result to
  // display" and never opens the slideout.
  return normalizeLegacy({ id: snap.id, ...snap.data() } as EligibilityCheckDoc)
}

/** Map an eligibility check's status to the cockpit display status.
 *  Uses the shared terminal-state classifier so the four buckets
 *  (eligible / ineligible / needs_review / error) stay consistent with Recent
 *  Checks and the failure slideout. `'not_eligible'` represents Ineligible.
 *
 *  Definitive coverage answers (eligible / ineligible) decay to `'stale'` once
 *  the visit the check was run for has passed. The date of service is resolved
 *  by `shared/eligibilityFreshness.resolveDateOfService`: the check's own
 *  `appointmentDate`, else the appointment's date when this is the appointment's
 *  own check (legacy), else `checkedAt`. We leave `needs_review` and `error`
 *  untouched — unresolved states whose required action doesn't change with age —
 *  and `stale` rows route to a re-check rather than the existing result (see
 *  DailyCockpit.handleRowClick).
 *
 *  `appointment` carries the Cockpit row's own check link + DOS for branches 2/3;
 *  omit it (e.g. Recent Checks) to judge by the check's own date alone. `now` is
 *  injectable for tests; callers in the app omit it. */
export function checkStatusToCockpit(
  check: EligibilityCheckDoc,
  appointment?: { eligibilityCheckId?: string | null; appointmentDate?: string | null },
  now: number = Date.now(),
): CockpitEligibilityStatus {
  if (check.status === 'pending') return 'in_progress'
  // Actively scheduled for automatic retry (transient payer / network / 5xx) →
  // the system is still working on it; no human action needed, so it must NOT
  // read as 'needs_review'. Shared helper so every status surface agrees. Once
  // the retry budget is spent, recovery is promoted to staff_review and the
  // check falls through to the terminal classification below.
  if (isAutoRetrying(check)) return 'in_progress'
  if (check.status === 'timeout' && (check.retryCount ?? 0) < MAX_RETRIES) return 'in_progress'
  const term = readTerminal(check)
  if (!term) return 'not_checked'
  switch (term.state) {
    case 'eligible':
    case 'ineligible':
      if (isCheckStale({
        checkAppointmentDate: check.appointmentDate,
        checkedAt: check.checkedAt,
        checkId: check.id,
        appointmentEligibilityCheckId: appointment?.eligibilityCheckId,
        appointmentDate: appointment?.appointmentDate,
      }, now)) return 'stale'
      return term.state === 'eligible' ? 'eligible' : 'not_eligible'
    case 'needs_review': return 'needs_review'
    case 'error': return 'error'
  }
}

const FIRESTORE_IN_LIMIT = 30

/**
 * Subscribe to the most recent eligibility check per flow (linear correction
 * chain). Queries all checks carrying each flowId, picks the newest by
 * checkedAt — a corrected re-run (even one whose identity edits landed in a
 * different dedup set) carries the same flowId, so the newest check IS the
 * current answer for the chain.
 *
 * `flowIds` are the appointment rows' flow keys (`flowId ?? setId` — see the
 * appointments translation layer). Legacy checks were backfilled with
 * `flowId := setId`, so the join holds across old and new data.
 * Uses `flowId in [...]` — works with default single-field index.
 */
export function subscribeToLatestChecksByFlowIds(
  orgId: string,
  flowIds: string[],
  onUpdate: (byFlowId: Map<string, EligibilityCheckDoc>) => void,
  clinicId?: string | null,
  onError?: (error: Error) => void,
): () => void {
  if (flowIds.length === 0) {
    onUpdate(new Map())
    return () => {}
  }

  const unique = [...new Set(flowIds)]
  const batches: string[][] = []
  for (let i = 0; i < unique.length; i += FIRESTORE_IN_LIMIT) {
    batches.push(unique.slice(i, i + FIRESTORE_IN_LIMIT))
  }

  const batchResults = new Map<number, EligibilityCheckDoc[]>()
  const unsubs: Unsubscribe[] = []

  function merge() {
    if (batchResults.size < batches.length) return
    const byFlowId = new Map<string, EligibilityCheckDoc>()
    for (const docs of batchResults.values()) {
      for (const d of docs) {
        const fid = d.flowId
        if (!fid) continue
        const existing = byFlowId.get(fid)
        // Keep the most recent check per flow (checkedAt is ISO string, lexicographic compare works)
        if (!existing || (d.checkedAt && existing.checkedAt && d.checkedAt > existing.checkedAt)) {
          byFlowId.set(fid, d)
        }
      }
    }
    onUpdate(byFlowId)
  }

  const handleError = (err: Error) => {
    Sentry.captureException(err)
    onError?.(err)
  }

  batches.forEach((batch, idx) => {
    // clinicId filter is required for non-admin reads — Firestore rules can't prove
    // `resource.data.clinicId == tokenClinicId()` without a matching query constraint.
    const constraints: QueryConstraint[] = [where('flowId', 'in', batch)]
    if (clinicId) constraints.push(where('clinicId', '==', clinicId))
    const q = query(orgEligibilityChecksCollection(orgId), ...constraints)
    const unsub = onSnapshot(q, (snap) => {
      batchResults.set(idx, snap.docs.map(d => normalizeLegacy({ id: d.id, ...d.data() } as EligibilityCheckDoc)))
      merge()
    }, handleError)
    unsubs.push(unsub)
  })

  return () => unsubs.forEach(u => u())
}

export async function submitCorrection(orgId: string, checkId: string, correction: CorrectionOverride): Promise<void> {
  const ref = doc(db, 'organizations', orgId, 'eligibilityChecks', checkId)
  await updateDoc(ref, { correction })
  logStaffEdit('eligibilityChecks', checkId, 'update')
}

export async function revertCorrection(orgId: string, checkId: string): Promise<void> {
  const ref = doc(db, 'organizations', orgId, 'eligibilityChecks', checkId)
  await updateDoc(ref, { correction: deleteField() })
  logStaffEdit('eligibilityChecks', checkId, 'update')
}

/**
 * Persist the staff member's selected payment option onto the check doc.
 * Survives re-runs of the rules engine because PaymentOption ids are
 * deterministic hashes. Allowed by firestore.rules alongside `correction`.
 */
export async function submitUserPick(orgId: string, checkId: string, userPick: UserPickOverride): Promise<void> {
  const ref = doc(db, 'organizations', orgId, 'eligibilityChecks', checkId)
  await updateDoc(ref, { userPick })
  logStaffEdit('eligibilityChecks', checkId, 'update')
}

/** Remove a previously selected payment option from the check doc. */
export async function clearUserPick(orgId: string, checkId: string): Promise<void> {
  const ref = doc(db, 'organizations', orgId, 'eligibilityChecks', checkId)
  await updateDoc(ref, { userPick: deleteField() })
  logStaffEdit('eligibilityChecks', checkId, 'update')
}

/** A correction chain is linear and short; this only bounds a corrupt loop. */
const MAX_CHAIN_HOPS = 10

/**
 * Where a chain read looks: the chain's flow key, and the clinic a non-admin
 * reader's query has to name (the rules cannot prove `clinicId` matches the
 * token without a matching constraint). Both null on legacy docs.
 */
export interface ChainScope {
  flowId: string | null
  clinicId: string | null
}

/**
 * Whatever the chain is asking NOW.
 *
 * An SMS recovery case is named after the chain's FIRST check and its id can
 * never move, so the drawer is always handed that original — which is why a
 * corrected case kept showing pre-correction values.
 *
 * Read by FLOW when the case has a flow key: every check in a chain carries the
 * same `flowId`, whoever minted it, and the newest by `checkedAt` is the current
 * answer — the same rule the Cockpit applies (`subscribeToLatestChecksByFlowIds`).
 * The `supersededBy` pointer is NOT enough on its own: only the staff and SMS
 * correction paths write it, while the DOS re-check sweep and the alias-probe
 * re-run mint same-flow successors that never carry it, and SMS re-runs from
 * before the pointer existed left theirs unstamped too. A walk down the pointer
 * stopped at the original for all three, and Save / Re-check then fired from a
 * check that already had a live successor.
 *
 * The flow's newest is then followed through its OWN `supersededBy`. The check
 * cache answers a correction from the whole identity SET, not the flow: a
 * same-identity check that is pending or freshly successful in another chain
 * (the same patient booked on adjacent days, a manual-form check the same day)
 * is reused instead of minted, the source is retired pointing at it, and it
 * never carries this flow's key — so the flow query alone landed back on the
 * retired source, the rows reverted to the pre-correction values and Save
 * re-armed, on every reopen. Following the pointer costs a read only when the
 * flow's newest was itself superseded, which is exactly that case.
 *
 * The pointer walk from the case's own check stays as the fallback for a case
 * with no flow key, or a flow the query finds empty (docs that predate the flow
 * backfill). A plain read per hop: the question is "what replaced this exact
 * doc" and the answer is a doc id already on the record. It returns the
 * starting check when nothing supersedes it, and stops on a missing link rather
 * than throwing — a broken chain should show the last check we can actually
 * read.
 *
 * Not a subscription either way: one read on open, one more from the check a
 * correction creates. A read that FAILS (permission, offline) rejects: the
 * caller cannot tell what it missed, so it must not treat whatever it holds as
 * the head. The Recovery drawer fails closed on it — its edit target has to be
 * the newest check, never one that may already be superseded.
 */
export async function resolveChainHead(
  orgId: string,
  checkId: string,
  scope?: ChainScope,
): Promise<EligibilityCheckDoc | null> {
  if (scope?.flowId) {
    const newest = await newestInFlow(orgId, scope.flowId, scope.clinicId)
    if (newest) return followSupersededBy(orgId, newest)
  }
  return walkSupersededBy(orgId, checkId)
}

/** The newest check carrying this flow key, or null when the flow holds none. */
async function newestInFlow(
  orgId: string,
  flowId: string,
  clinicId: string | null,
): Promise<EligibilityCheckDoc | null> {
  const constraints: QueryConstraint[] = [where('flowId', '==', flowId)]
  if (clinicId) constraints.push(where('clinicId', '==', clinicId))
  const snap = await getDocs(query(orgEligibilityChecksCollection(orgId), ...constraints))
  let newest: EligibilityCheckDoc | null = null
  for (const d of snap.docs) {
    const check = normalizeLegacy({ id: d.id, ...d.data() } as EligibilityCheckDoc)
    // `checkedAt` is stamped at mint and re-stamped by a same-doc retry (the
    // sweep's `retryOnDoc`), so the newest ISO string is the check most
    // recently put to the payer. A doc without one sorts oldest.
    if (!newest || (check.checkedAt ?? '') > (newest.checkedAt ?? '')) newest = check
  }
  return newest
}

async function walkSupersededBy(
  orgId: string,
  checkId: string,
): Promise<EligibilityCheckDoc | null> {
  const start = await getEligibilityCheck(orgId, checkId)
  return start ? followSupersededBy(orgId, start) : null
}

/** From a doc already in hand: what replaced it, hop by hop. Reads nothing
 *  when nothing did. */
async function followSupersededBy(
  orgId: string,
  start: EligibilityCheckDoc,
): Promise<EligibilityCheckDoc> {
  let current = start
  for (let hop = 0; hop < MAX_CHAIN_HOPS; hop++) {
    const next = current.supersededBy
    if (!next || next === current.id) return current
    const doc = await getEligibilityCheck(orgId, next)
    if (!doc) return current
    current = doc
  }
  return current
}

/** Input shape for verifyEligibility (matches the cloud function input). */
export interface VerifyEligibilityInput {
  orgId: string
  clinicId: string
  providerId: string
  subscriber: {
    firstName: string
    lastName: string
    memberId: string
    dateOfBirth: string
  }
  tradingPartnerServiceId: string
  /** Stedi stable `stediId` for the selected payer — drives Original-Medicare routing. */
  stediPayerId?: string | null
  payerName?: string
  /** Flow (linear correction chain) the check joins — set when correcting an
   *  existing check / re-checking a cockpit row. Absent → new flow. */
  flowId?: string
  /** The check being corrected/re-checked — the server inherits patient
   *  contact + SMS-recovery candidacy from it (never from the form). Absent →
   *  a fresh manual lookup: no contact stored, never enters the SMS loop. */
  sourceCheckId?: string
  /** The cockpit appointment row this check was launched from, set only when
   *  that row carries no check yet (nothing for the server's relink to find).
   *  The resulting check is stamped onto it so the row stops reading
   *  "Cannot check" after staff have answered it. */
  appointmentId?: string
}

/** Runs a manual / one-off eligibility check (`verifyEligibility` callable). */
export async function verifyEligibility(input: VerifyEligibilityInput): Promise<VerifyEligibilityResponse> {
  const fn = httpsCallable<VerifyEligibilityInput, VerifyEligibilityResponse>(functions, 'verifyEligibility')
  const res = await fn(input)
  return res.data
}
